import { datadogRum } from '@datadog/browser-rum';
import useSWR from 'swr';
import { validate as isUuid } from 'uuid';

export type InteractionScopedFlags = {
  txmMoveComplianceDataUpstream?: boolean;
};

/**
 * Evaluate the interaction-scoped feature flags on behalf of the
 * interaction's primary agent — used where a rollout must follow the agent
 * rather than the viewer, so a TC and the agent see the same behavior on the
 * same transaction.
 *
 * Resolution goes through `/api/launch-darkly/interaction-flags`, which
 * derives the owner server-side from an interaction the caller can access —
 * the client never supplies the identity to evaluate against.
 * While values (or the interaction id) are still loading, the flags are
 * undefined and `loading` is true — treat that as "unknown", not off.
 */
export function useInteractionFlags(interactionId: string | null | undefined) {
  const shouldFetch = !!interactionId && isUuid(interactionId);
  const { data, isLoading, error } = useSWR(
    shouldFetch
      ? `${process.env.NEXT_PUBLIC_BASE_PATH}/api/launch-darkly/interaction-flags?interactionId=${interactionId}`
      : null,
    async (url: string) => {
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        // statusCode lets the global shouldRetryOnError skip retries on 4xx.
        const error = Object.assign(
          new Error('Failed to evaluate interaction flags'),
          { statusCode: response.status },
        );
        datadogRum.addError(error, {
          context: 'useInteractionFlags',
          status: response.status,
          interactionId,
        });
        throw error;
      }
      return response.json() as Promise<InteractionScopedFlags>;
    },
    { revalidateOnFocus: false },
  );

  return {
    flags: data,
    loading: isLoading,
    error,
  };
}
